import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import {
    formatCasesAndLooseSummary,
    formatExpectedBbd,
    formatExpectedQtySummary,
    formatExpectedUnitsPerCase,
} from 'components/warehouse/components/fcTransfers/acceptTransfer/acceptTransferLineDisplayUtils';
import {
    buildInitialDraftFromItem,
    getExpectedReceiptSnapshot,
    getReceiptMismatchWarnings,
    receivedQuantitiesForPalletCount,
    validateAcceptTransferLineDraft,
} from 'components/warehouse/components/fcTransfers/acceptTransfer/acceptTransferLineReceiptDraft';
import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';

/**
 * Per-line receive/edit modal: capture pallet breakdown, BBD, and count/case before calling save-line API.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {Record<string, unknown> | null} props.item
 * @param {boolean} [props.isSubmitting=false]
 * @param {string | null} [props.submitError=null]
 * @param {() => void} [props.onClearSubmitError]
 * @param {(draft: Record<string, unknown>) => void | Promise<void>} props.onSubmit
 */
export function AcceptTransferLineReceiveModal({
    isSubmitting = false,
    item,
    onClearSubmitError,
    onOpenChange,
    onSubmit,
    open,
    submitError = null,
}) {
    const [draft, setDraft] = useState(() => ({}));
    const [validationError, setValidationError] = useState(null);

    const itemPk = item?.id ?? item?.transferItemId;
    const seededSessionRef = useRef(null);

    useEffect(() => {
        if (!open) {
            seededSessionRef.current = null;
            return;
        }
        if (itemPk === undefined || itemPk === null || !item) {
            return;
        }
        const sessionKey = String(itemPk);
        if (seededSessionRef.current !== sessionKey) {
            setDraft(buildInitialDraftFromItem(item));
            setValidationError(null);
            seededSessionRef.current = sessionKey;
        }
    }, [open, item, itemPk]);

    const expectedSnapshot = useMemo(() => getExpectedReceiptSnapshot(item), [item]);

    const mismatchWarnings = useMemo(
        () => getReceiptMismatchWarnings(draft, expectedSnapshot),
        [draft, expectedSnapshot],
    );

    const palletTotals = useMemo(() => {
        const rows = Array.isArray(draft?.receivedQuantities) ? draft.receivedQuantities : [];
        let sumCases = 0;
        let sumLoose = 0;
        for (const row of rows) {
            if (row && typeof row === 'object') {
                const rec = /** @type {Record<string, unknown>} */ (row);
                sumCases += Math.trunc(Number(rec.numFullCases)) || 0;
                sumLoose += Math.trunc(Number(rec.numLooseUnits)) || 0;
            }
        }
        return { sumCases, sumLoose };
    }, [draft?.receivedQuantities]);

    const patchDraft = useCallback(
        (patch) => {
            onClearSubmitError?.();
            setValidationError(null);
            setDraft((prev) => ({ ...prev, ...patch }));
        },
        [onClearSubmitError],
    );

    const handleNumPalletsChange = useCallback(
        (value) => {
            onClearSubmitError?.();
            setValidationError(null);
            const trimmed = String(value).trim();
            if (trimmed === '') {
                setDraft((prev) => ({ ...prev, numPallets: trimmed }));
                return;
            }
            const parsed = Number.parseInt(trimmed, 10);
            if (!Number.isFinite(parsed) || parsed < 1) {
                setDraft((prev) => ({ ...prev, numPallets: trimmed }));
                return;
            }
            const n = Math.max(1, Math.floor(parsed));
            const nextRows = receivedQuantitiesForPalletCount(
                expectedSnapshot.expectedCases,
                expectedSnapshot.expectedLoose,
                n,
            );
            setDraft((prev) => ({
                ...prev,
                numPallets: String(n),
                receivedQuantities: nextRows,
            }));
        },
        [expectedSnapshot.expectedCases, expectedSnapshot.expectedLoose, onClearSubmitError],
    );

    const patchPalletRow = useCallback(
        (index, partial) => {
            onClearSubmitError?.();
            setValidationError(null);
            setDraft((prev) => {
                const rows = Array.isArray(prev.receivedQuantities) ? [...prev.receivedQuantities] : [];
                const existing =
                    rows[index] && typeof rows[index] === 'object'
                        ? /** @type {Record<string, unknown>} */ (rows[index])
                        : {};
                rows[index] = { ...existing, ...partial };
                return { ...prev, receivedQuantities: rows };
            });
        },
        [onClearSubmitError],
    );

    const handleSubmit = useCallback(() => {
        const message = validateAcceptTransferLineDraft(draft);
        if (message) {
            setValidationError(message);
            return;
        }
        setValidationError(null);
        onSubmit?.(draft);
    }, [draft, onSubmit]);

    const lotLabel = item?.lotId ? `Lot: ${item.lotId}` : '';
    const productLabel = item?.itemName ?? '';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="flex max-h-[90vh] max-w-[calc(100vw-2rem)] flex-col gap-3 overflow-hidden p-6 sm:max-w-3xl"
                onPointerDownOutside={(e) => {
                    if (isSubmitting) {
                        e.preventDefault();
                    }
                }}
                onEscapeKeyDown={(e) => {
                    if (isSubmitting) {
                        e.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle>Receive line</DialogTitle>
                    <DialogDescription>
                        {[lotLabel, productLabel].filter(Boolean).length > 0 ? (
                            <>
                                {' '}
                                <span className="font-medium text-foreground">
                                    {[lotLabel, productLabel].filter(Boolean).join(' · ')}
                                </span>
                            </>
                        ) : null}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid min-h-0 flex-1 gap-3 overflow-y-auto py-1">
                    <div className="rounded-md border border-border bg-muted/40 px-2 py-2 text-sm">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                            <span className="shrink-0 font-semibold text-foreground">Expected</span>
                            <span className="text-muted-foreground">
                                Qty{' '}
                                <span className="font-medium tabular-nums text-foreground">
                                    {formatExpectedQtySummary(item)}
                                </span>
                            </span>
                            <span className="text-muted-foreground">
                                BBD <span className="font-medium text-foreground">{formatExpectedBbd(item)}</span>
                            </span>
                            <span className="text-muted-foreground">
                                Ct/cs{' '}
                                <span className="font-medium tabular-nums text-foreground">
                                    {formatExpectedUnitsPerCase(item)}
                                </span>
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-12 sm:items-end">
                        <div className="grid gap-1.5 sm:col-span-5">
                            <Label className="text-xs" htmlFor="receive-bbd">
                                Actual best-by
                            </Label>
                            <Input
                                id="receive-bbd"
                                aria-label="Actual best-by date"
                                className="h-9"
                                type="date"
                                value={draft.receivedExpiresOn ?? ''}
                                onChange={(e) => patchDraft({ receivedExpiresOn: e.target.value })}
                            />
                        </div>
                        <div className="grid gap-1.5 sm:col-span-4">
                            <Label className="text-xs" htmlFor="receive-upc">
                                Actual ct/cs
                            </Label>
                            <Input
                                id="receive-upc"
                                aria-label="Actual count per case"
                                className="h-9"
                                min={1}
                                step={1}
                                type="number"
                                value={draft.receivedUnitsPerCase ?? ''}
                                onChange={(e) => patchDraft({ receivedUnitsPerCase: e.target.value })}
                            />
                        </div>
                        <div className="grid gap-1.5 sm:col-span-3">
                            <Label className="text-xs" htmlFor="receive-pallets">
                                # Pallets
                            </Label>
                            <Input
                                id="receive-pallets"
                                aria-label="Number of pallets"
                                className="h-9"
                                min={1}
                                step={1}
                                type="number"
                                value={draft.numPallets ?? ''}
                                onChange={(e) => handleNumPalletsChange(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                            <p className="text-sm font-medium text-foreground">Per pallet</p>
                            <p className="text-xs text-muted-foreground tabular-nums sm:text-sm" aria-live="polite">
                                Total: {formatCasesAndLooseSummary(palletTotals.sumCases, palletTotals.sumLoose)}
                            </p>
                        </div>
                        <div className="grid grid-cols-[3rem_1fr_1fr] gap-x-2 border-b border-border pb-1 text-xs font-medium text-muted-foreground">
                            <span className="tabular-nums" aria-hidden>
                                #
                            </span>
                            <span>Cases</span>
                            <span>Loose units</span>
                        </div>
                        <div className="max-h-[min(38vh,14rem)] min-h-0 overflow-y-auto overscroll-contain pr-1">
                            <div className="grid gap-1">
                                {Array.isArray(draft.receivedQuantities) &&
                                    draft.receivedQuantities.map((row, index) => {
                                        const rec =
                                            row && typeof row === 'object'
                                                ? /** @type {Record<string, unknown>} */ (row)
                                                : {};
                                        return (
                                            <div
                                                className="grid grid-cols-[3rem_1fr_1fr] items-center gap-x-2 rounded border border-border bg-card px-2 py-1"
                                                key={index} // eslint-disable-line react/no-array-index-key -- positional pallet slots
                                            >
                                                <span
                                                    className="text-xs font-medium text-muted-foreground tabular-nums"
                                                    title={`Pallet ${index + 1}`}
                                                >
                                                    {index + 1}
                                                </span>
                                                <Input
                                                    aria-label={`Pallet ${index + 1} cases`}
                                                    className="h-8"
                                                    id={`pallet-${index}-cases`}
                                                    min={0}
                                                    step={1}
                                                    type="number"
                                                    value={rec.numFullCases ?? ''}
                                                    onChange={(e) =>
                                                        patchPalletRow(index, {
                                                            numFullCases: e.target.value,
                                                        })
                                                    }
                                                />
                                                <Input
                                                    aria-label={`Pallet ${index + 1} loose units`}
                                                    className="h-8"
                                                    id={`pallet-${index}-loose`}
                                                    min={0}
                                                    step={1}
                                                    type="number"
                                                    value={rec.numLooseUnits ?? ''}
                                                    onChange={(e) =>
                                                        patchPalletRow(index, {
                                                            numLooseUnits: e.target.value,
                                                        })
                                                    }
                                                />
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>

                    {mismatchWarnings.length > 0 ? (
                        <ul
                            className="list-disc space-y-1 pl-5 text-sm text-amber-700 dark:text-amber-400"
                            role="status"
                            aria-label="Receipt mismatch warnings"
                        >
                            {mismatchWarnings.map((w) => (
                                <li key={w.id}>{w.message}</li>
                            ))}
                        </ul>
                    ) : null}

                    {validationError ? (
                        <p className="text-sm text-destructive" role="alert">
                            {validationError}
                        </p>
                    ) : null}
                    {submitError ? (
                        <p className="text-sm text-destructive" role="alert">
                            {submitError}
                        </p>
                    ) : null}
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        type="button"
                        variant="outline"
                        disabled={isSubmitting}
                        onClick={() => onOpenChange?.(false)}
                    >
                        Cancel
                    </Button>
                    <Button type="button" disabled={isSubmitting} onClick={handleSubmit}>
                        {isSubmitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                                Saving…
                            </>
                        ) : (
                            'Save line'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

AcceptTransferLineReceiveModal.displayName = 'AcceptTransferLineReceiveModal';
